/**
 * The agent data map in the console (ADR-0071): what data exists at each step, its shape and
 * where it comes from, and the fields the Builder offers wherever a step refers to data.
 *
 * The control plane computes the authoritative map with the SDK (`POST /v1/specs/data-map`,
 * with the tool registry's output shapes, other agents' values and the data checks). The console
 * also computes a local map right away from the spec and the tool catalogue (same rules, no
 * checks) so intellisense never waits for the network; the server's map replaces it when it
 * arrives.
 */
import type { RegistryTool, Text } from "../types";
import { formulaSchema } from "./formula";
import { humaniseName } from "./humanise";
import { badTemplates } from "./templateFix";
import type { AgentSpec, SpecNode } from "./types";

export type Schema = Record<string, unknown>;
export type Availability = "always" | "maybe";
export type Shape = "platform" | "declared" | "tool" | "model" | "ai_text" | "answer" | "sample" | "agent" | "combined" | "unknown";

export interface DataValue {
  /** State key; templates use `state.<key>`. */
  key: string;
  /** The step that makes it; null for start values. */
  node: string | null;
  kind: string;
  label: Text;
  schema: Schema;
  shape: Shape | string;
}

export interface DataIssue {
  code: string;
  severity: "error" | "warning";
  node: string | null;
  /** Where in the step: `args.urls`, `edge a->b`, `routes[0]`, `approvals[1].when`, … */
  where: string;
  path: string | null;
  suggestion: string | null;
  message: Text;
}

export interface DataMap {
  values: DataValue[];
  /** Per step: values that can exist when it runs. */
  before: Record<string, Record<string, Availability>>;
  /** Per step: values that can exist when it is done (the arrows leaving it). */
  after: Record<string, Record<string, Availability>>;
  inputs: string[];
  subagent_inputs: Record<string, string[]>;
  issues: DataIssue[];
}

// ---------------------------------------------------------------- schemas

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

function alternatives(s: Schema): Schema[] {
  for (const key of ["anyOf", "oneOf"]) {
    const list = s[key];
    if (Array.isArray(list)) return list.filter((m): m is Schema => isObj(m) && m.type !== "null").flatMap(alternatives);
  }
  return [s];
}

function jsonType(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "boolean") return "boolean";
  if (typeof v === "number") return Number.isInteger(v) ? "integer" : "number";
  if (typeof v === "string") return "string";
  if (Array.isArray(v)) return "array";
  return "object";
}

/** The JSON types a schema allows; null when anything / unknown. */
export function schemaTypes(s: Schema | null | undefined): string[] | null {
  if (!s || !Object.keys(s).length) return null;
  const alts = alternatives(s);
  if (alts.length !== 1 || alts[0] !== s) {
    const out = new Set<string>();
    for (const a of alts) {
      const t = schemaTypes(a);
      if (!t) return null;
      t.forEach((x) => out.add(x));
    }
    return out.size ? [...out] : null;
  }
  if ("const" in s) return [jsonType(s.const)];
  if (Array.isArray(s.enum) && s.enum.length) return [...new Set(s.enum.map(jsonType))];
  if (typeof s.type === "string") return [s.type];
  if (Array.isArray(s.type)) return s.type.map(String);
  if (isObj(s.properties)) return ["object"];
  if (isObj(s.items)) return ["array"];
  return null;
}

/** Values a schema lists (`enum` / `const`), else null. */
export function knownValues(s: Schema | null | undefined): unknown[] | null {
  if (!s || !Object.keys(s).length) return null;
  const out: unknown[] = [];
  for (const a of alternatives(s)) {
    if ("const" in a) out.push(a.const);
    else if (Array.isArray(a.enum)) out.push(...a.enum);
    else return null;
  }
  return out;
}

/** The schema of a field (or list item) inside `s`, or null when unknown. */
export function childSchema(s: Schema | null | undefined, seg: string | number): Schema | null {
  if (!s) return null;
  const found: Schema[] = [];
  for (const a of alternatives(s)) {
    if (typeof seg === "number") {
      if (isObj(a.items)) found.push(a.items);
      continue;
    }
    const props = a.properties;
    if (isObj(props) && isObj(props[seg])) found.push(props[seg] as Schema);
    else if (isObj(a.additionalProperties)) found.push(a.additionalProperties);
  }
  if (!found.length) return null;
  return found.length === 1 ? (found[0] as Schema) : { anyOf: found };
}

/** A schema at a dotted path (`request.pages`, `pages[0].url`) inside a value's schema. */
export function schemaAt(root: Schema | null | undefined, rest: readonly (string | number)[]): Schema | null {
  let cur: Schema | null = root ?? null;
  for (const seg of rest) {
    cur = childSchema(cur, seg);
    if (!cur) return null;
  }
  return cur;
}

/** A JSON Schema describing a sample value (a test run's result, a sample event). */
export function inferSchema(sample: unknown, depth = 0): Schema {
  if (depth > 8) return {};
  if (sample === null || sample === undefined) return { type: "null" };
  if (typeof sample === "boolean") return { type: "boolean" };
  if (typeof sample === "number") return { type: Number.isInteger(sample) ? "integer" : "number" };
  if (typeof sample === "string") return { type: "string" };
  if (Array.isArray(sample)) {
    const items = sample.slice(0, 20).map((x) => inferSchema(x, depth + 1));
    const merged = mergeInferred(items);
    return merged ? { type: "array", items: merged } : { type: "array" };
  }
  return { type: "object", properties: Object.fromEntries(Object.entries(sample as Record<string, unknown>).map(([k, v]) => [k, inferSchema(v, depth + 1)])) };
}

function mergeInferred(items: Schema[]): Schema | null {
  if (!items.length) return null;
  const first = JSON.stringify(items[0]);
  if (items.every((i) => JSON.stringify(i) === first)) return items[0] as Schema;
  if (items.every((i) => i.type === "object")) {
    const props: Record<string, Schema> = {};
    for (const i of items) for (const [k, v] of Object.entries((i.properties as Record<string, Schema>) ?? {})) props[k] ??= v;
    return { type: "object", properties: props };
  }
  const seen = new Set<string>();
  return { anyOf: items.filter((i) => (seen.has(JSON.stringify(i)) ? false : (seen.add(JSON.stringify(i)), true))) };
}

function union(schemas: Schema[]): Schema {
  const seen = new Map<string, Schema>();
  for (const s of schemas) seen.set(JSON.stringify(s), s);
  const list = [...seen.values()];
  if (!list.length || list.some((s) => !Object.keys(s).length)) return {};
  return list.length === 1 ? (list[0] as Schema) : { anyOf: list };
}

// ---------------------------------------------------------------- the local map (mirrors the SDK)

const QUESTION: Schema = { type: "string", description: "What the person asked" };
const LANG: Schema = { type: "string", enum: ["ar", "en"] };
const DOCUMENTS: Schema = { type: "array", items: { type: "string" } };
const PREVIOUS: Schema = { type: "object", description: "What this agent left at its last turn of the same conversation (its conversation_state); empty on the first turn" };
const VISITS: Schema = { type: "object", description: "How many times each step has run so far in this run, by step id", additionalProperties: { type: "integer", minimum: 0 } };
const PASSAGES: Schema = {
  type: "array",
  items: { type: "object", properties: { text: { type: "string" }, score: { type: "number" }, classification: { type: "string" }, attribution: { type: "object" } } },
};
const APPROVAL: Schema = { type: "string", enum: ["approve", "reject", "edit"] };
const COMMENT: Schema = { type: ["string", "null"], description: "What the approver wrote with their decision (empty when nothing)" };
const ERROR: Schema = { type: "string", description: "What went wrong, when the call failed (empty when it worked)" };

/** A system step's result also carries `error` when the call failed (as the SDK's `_with_error`, ADR-0079). */
function withError(schema: Schema): Schema {
  const props = isObj(schema.properties) ? (schema.properties as Record<string, Schema>) : {};
  if (schema.type !== "object" || "error" in props) return schema;
  return { ...schema, properties: { ...props, error: ERROR } };
}
const TOOL_REQUEST: Schema = { type: "object", properties: { id: { type: "string" }, name: { type: "string" }, tool: { type: "string" }, action: { type: "string" }, args: { type: "object" } } };
const MESSAGES: Schema = { type: "array", items: { type: "object" } };
const AI_TEXT: Schema = { type: "string", description: "The AI model's answer, as text" };
const PAYLOAD_TRIGGERS = new Set(["webhook", "queue", "email", "file_drop", "message"]);

const KIND_LABEL: Record<string, [string, string]> = {
  result: ["Result of ‘{s}’", "نتيجة ‘{s}’"],
  answer: ["Answer of ‘{s}’", "إجابة ‘{s}’"],
  documents: ["Passages found by ‘{s}’", "المقاطع التي وجدتها ‘{s}’"],
  reply: ["Reply to ‘{s}’", "الرد على ‘{s}’"],
  approval: ["Decision in ‘{s}’", "القرار في ‘{s}’"],
  approval_comment: ["Approver's comment in ‘{s}’", "تعليق المعتمِد في ‘{s}’"],
  decision: ["Choice made in ‘{s}’", "الاختيار في ‘{s}’"],
  subagent: ["Result of ‘{s}’", "نتيجة ‘{s}’"],
  combined: ["Combined results of ‘{s}’", "النتائج المجمّعة في ‘{s}’"],
  answers: ["All answers of ‘{s}’", "كل إجابات ‘{s}’"],
  tool_request: ["Tool call chosen by ‘{s}’", "استدعاء الأداة الذي اختارته ‘{s}’"],
  messages: ["Messages so far", "الرسائل حتى الآن"],
  plain_answer: ["The answer from ‘{s}’", "الإجابة من ‘{s}’"],
  reply_choice: ["Button clicked in ‘{s}’", "الزر الذي نُقر في ‘{s}’"],
  wait: ["What ended ‘{s}’", "ما أنهى ‘{s}’"],
  calculated: ["Worked out in ‘{s}’", "محسوبة في ‘{s}’"],
  foreach_results: ["Results of ‘{s}’ for each item", "نتائج ‘{s}’ لكل عنصر"],
  loop_item: ["The item ‘{s}’ is working on", "العنصر الذي تعمل عليه ‘{s}’"],
  table: ["Table from ‘{s}’", "الجدول من ‘{s}’"],
  calc_problems: ["What ‘{s}’ could not work out", "ما لم تستطع ‘{s}’ حسابه"],
};

/** What a "Wait for" step leaves for later steps (ADR-0094; the SDK's `wait_schema`). */
export function waitSchema(n: SpecNode): Schema {
  const lab = (en: string, ar: string) => ({ en, ar });
  const props: Record<string, Schema> = {
    by: { type: "string", enum: ["event", "answer", "return", "time", "gave_up"], "x-label": lab("What ended the wait", "ما أنهى الانتظار") },
    at: { type: "string", format: "date-time", "x-label": lab("When", "متى") },
    gave_up: { type: "boolean", "x-label": lab("Nothing came in time", "لم يصل شيء في الوقت") },
  };
  if (n.event) {
    props.data = { ...(isObj(n.event.data_schema) ? n.event.data_schema : { type: "object" }), "x-label": lab("The event's values", "قيم الحدث") };
    props.event_id = { type: ["string", "null"], "x-label": lab("Event id", "رقم الحدث") };
  } else {
    props.data = { type: "object", "x-label": lab("Values sent back", "القيم المُعادة") };
  }
  if (n.answer) {
    props.answer = { ...(isObj(n.answer.answer_schema) ? n.answer.answer_schema : { type: "string" }), "x-label": lab("The person's answer", "إجابة الشخص") };
    props.answered_by = { type: ["string", "null"], "x-label": lab("Who answered", "من أجاب") };
  }
  return { type: "object", properties: props, required: ["by", "at"] };
}
/** What a click on a waiting rich reply leaves for later steps (ADR-0088; the SDK's CHOICE_SCHEMA). */
const REPLY_CHOICE: Schema = {
  type: "object",
  properties: {
    button: { type: "string", "x-label": { en: "Button", ar: "الزر" } },
    value: { type: ["string", "null"], "x-label": { en: "Value", ar: "القيمة" } },
    item: { "x-label": { en: "Chosen item", ar: "العنصر المختار" } },
    form: { type: ["object", "null"], "x-label": { en: "Form", ar: "النموذج" } },
  },
};

function nodeName(n: SpecNode, lang: "ar" | "en"): string {
  const l = n.label ? n.label[lang] || n.label.en || n.label.ar : "";
  return l || humaniseName(n.id);
}

function labelOf(n: SpecNode, kind: string): Text {
  if (n.output_label && (n.output_label.en || n.output_label.ar)) return n.output_label;
  const [en, ar] = KIND_LABEL[kind] ?? KIND_LABEL.result ?? ["{s}", "{s}"];
  return { en: en.replace("{s}", nodeName(n, "en")), ar: ar.replace("{s}", nodeName(n, "ar")) };
}

function decisionSchema(n: SpecNode): Schema {
  let value: Schema;
  if (n.kind === "boolean") value = { type: "boolean" };
  else if (n.kind === "score") value = { type: "number", minimum: n.rubric?.min, maximum: n.rubric?.max };
  else value = { type: "string", enum: (n.options ?? []).map((o) => o.value) };
  return {
    type: "object",
    properties: {
      value: { ...value, description: "The choice" },
      label: { type: "object", description: "The choice in plain words" },
      probability: { type: "number", minimum: 0, maximum: 1, description: "How sure the model is (0 to 1)" },
      by: { type: "string", enum: ["model", "person"] },
      low_confidence: { type: "boolean" },
      needs_person: { type: "boolean" },
    },
  };
}

function toolOutput(n: SpecNode, tools: readonly RegistryTool[]): { schema: Schema; shape: Shape } {
  if (isObj(n.output_schema)) return { schema: n.output_schema, shape: "declared" };
  const manifest = tools.find((t) => t.ref === n.tool)?.actions.find((a) => a.name === n.action)?.output_schema;
  if (!manifest) return { schema: {}, shape: "unknown" };
  const field = typeof n.output_field === "string" && n.output_field ? n.output_field : null;
  const schema = field ? (childSchema(manifest, field) ?? {}) : manifest;
  return { schema, shape: Object.keys(schema).length ? "tool" : "unknown" };
}

function writes(n: SpecNode, spec: AgentSpec, tools: readonly RegistryTool[]): DataValue[] {
  const v = (key: string, kind: string, schema: Schema, shape: Shape): DataValue => ({ key, node: n.id, kind, label: labelOf(n, kind), schema, shape });
  const out = typeof n.output_key === "string" && n.output_key ? n.output_key : null;
  switch (n.type) {
    case "llm": {
      const list = [v("messages", "messages", MESSAGES, "platform")];
      const key = out ?? "messages";
      const model = (isObj(n.model) ? n.model : spec.spec.model) as { output_schema?: unknown };
      if (key !== "messages") {
        if (isObj(model.output_schema)) list.unshift(v(key, "answer", model.output_schema, "model"));
        else list.unshift(v(key, "answer", AI_TEXT, "ai_text"));
      } else if (!isObj(model.output_schema)) {
        // A plain answer can be used by later steps (an e-mail's body, G-io-19): choosing it
        // keeps the step's text under `<step>_answer` too (see `withAnswerKeys`).
        list.unshift(v(answerKey(n.id), "plain_answer", AI_TEXT, "ai_text"));
      }
      if ((n.tools ?? []).length) list.push(v("tool_call", "tool_request", TOOL_REQUEST, "platform"));
      // A loop's answers add up in a list (G-flow-07).
      const collect = typeof n.add_to_list === "string" && n.add_to_list ? n.add_to_list : null;
      const answer = list[0];
      if (collect && key !== "messages" && answer) list.push(v(collect, "answers", { type: "array", items: answer.schema }, answer.shape as Shape));
      const reply = n.reply?.blocks ?? [];
      const runsStep = reply.some((b) => (b.kind === "quick_replies" ? b.options : b.kind === "buttons" ? b.buttons : b.kind === "cards" ? (b.buttons ?? []) : []).some((x) => x.action.type === "action" || x.action.type === "handoff"));
      if (runsStep) {
        const [en, ar] = KIND_LABEL.reply_choice as [string, string];
        list.push({ key: `${n.id}_choice`, node: n.id, kind: "reply_choice", label: { en: en.replace("{s}", nodeName(n, "en")), ar: ar.replace("{s}", nodeName(n, "ar")) }, schema: REPLY_CHOICE, shape: "platform" });
      }
      return list;
    }
    case "tool": {
      const { schema, shape } = toolOutput(n, tools);
      return [v(out ?? `${n.id}_result`, "result", withError(schema), shape)];
    }
    case "retrieval":
      return [v(out ?? "context", "documents", PASSAGES, "platform")];
    case "human_input":
      return out ? [v(out, "reply", isObj(n.answer_schema) ? n.answer_schema : { type: "string" }, "answer")] : [];
    case "human_approval":
      return [v(`${n.id}_decision`, "approval", APPROVAL, "platform"), v(`${n.id}_comment`, "approval_comment", COMMENT, "platform")];
    case "decision":
      return [v(out ?? n.id, "decision", decisionSchema(n), "platform")];
    case "subgraph":
      return [v(out ?? "subgraph_result", "subagent", { type: "object" }, "unknown")];
    case "wait":
      return [v(out ?? `${n.id}_result`, "wait", waitSchema(n), "platform")];
    case "table":
      return [v(out ?? `${n.id}_table`, "table", tableSchema(n), "platform")];
    case "foreach":
      // ADR-0104: one entry per item; the item's own shape is filled in by `loopValues`.
      return [v(out ?? `${n.id}_results`, "foreach_results", foreachSchema({}, {}), "platform")];
    case "calculate": {
      // ADR-0103: each value under its own name, its shape from its formula.
      const made: DataValue[] = (n.values ?? []).map((c) => ({ key: c.key, node: n.id, kind: "calculated", label: c.label && (c.label.en || c.label.ar) ? { en: c.label.en || c.label.ar, ar: c.label.ar || c.label.en } : { en: humaniseName(c.key), ar: humaniseName(c.key) }, schema: formulaSchema(c.expr), shape: "declared" as Shape }));
      if (n.on_error === "continue") made.push(v(`${n.id}_problems`, "calc_problems", { type: "array", items: { type: "object", properties: { value: { type: "string" }, reason: { type: "string" } } } }, "platform"));
      return made;
    }
    case "join": {
      if (n.strategy !== "list" && n.strategy !== "vote") return [];
      const par = spec.spec.graph.nodes.find((p) => p.type === "parallel" && p.join === n.id);
      const items = union(
        (par?.branches ?? [])
          .map((b) => spec.spec.graph.nodes.find((x) => x.id === b))
          .filter((x): x is SpecNode => !!x)
          .flatMap((x) => writes(x, spec, tools).slice(0, 1).map((w) => w.schema)),
      );
      return [v(n.id, "combined", n.strategy === "list" ? { type: "array", items } : items, "combined")];
    }
    default:
      return [];
  }
}

/** Where an AI step's plain answer is kept when a later step uses it (G-io-19). */
export function answerKey(step: string): string {
  return `${step}_answer`;
}

/**
 * An AI step that only answers in the conversation keeps its text nowhere a later step can
 * read. When another part of the agent uses `<step>_answer`, the step is set to keep its answer
 * there as well (it is still shown to the person). Returns the same object when nothing changes.
 */
export function withAnswerKeys(spec: AgentSpec): AgentSpec {
  const plain = spec.spec.graph.nodes.filter((n) => {
    if (n.type !== "llm" || (n.output_key && n.output_key !== "messages")) return false;
    const model = (isObj(n.model) ? n.model : spec.spec.model) as { output_schema?: unknown };
    return !isObj(model.output_schema);
  });
  if (!plain.length) return spec;
  const text = JSON.stringify(spec.spec.graph.nodes.map((n) => ({ ...n, output_key: undefined })));
  const used = plain.filter((n) => new RegExp(`\\b${answerKey(n.id).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(text));
  if (!used.length) return spec;
  const ids = new Set(used.map((n) => n.id));
  return {
    ...spec,
    spec: { ...spec.spec, graph: { ...spec.spec.graph, nodes: spec.spec.graph.nodes.map((n) => (ids.has(n.id) ? { ...n, output_key: answerKey(n.id) } : n)) } },
  };
}

function startValues(spec: AgentSpec): DataValue[] {
  const out: DataValue[] = [
    { key: "question", node: null, kind: "question", label: { en: "What the person asked", ar: "ما سأله الشخص" }, schema: QUESTION, shape: "platform" },
    { key: "lang", node: null, kind: "lang", label: { en: "Language of the question", ar: "لغة السؤال" }, schema: LANG, shape: "platform" },
    { key: "document_ids", node: null, kind: "documents", label: { en: "Attached documents", ar: "المستندات المرفقة" }, schema: DOCUMENTS, shape: "platform" },
    { key: "previous", node: null, kind: "previous", label: { en: "What it kept from its last turn", ar: "ما احتفظ به من دوره السابق" }, schema: PREVIOUS, shape: "platform" },
  ];
  if (spec.spec.graph.nodes.some((n) => n.max_iterations)) {
    // A step with a repeat limit: conditions can count its runs ("or 3 tries"), as the SDK.
    out.push({ key: "visits", node: null, kind: "visits", label: { en: "How many times each step has run", ar: "عدد مرات تنفيذ كل خطوة" }, schema: VISITS, shape: "platform" });
  }
  const triggers = ((spec.spec.triggers ?? []) as { type?: string; config?: Record<string, unknown> }[]).filter((t) => PAYLOAD_TRIGGERS.has(String(t.type)));
  if (triggers.length) {
    const samples = triggers.map((t) => t.config?.sample).filter((s) => s !== undefined && s !== null);
    out.push({
      key: "trigger",
      node: null,
      kind: "trigger",
      label: { en: "The event that started it", ar: "الحدث الذي بدأه" },
      schema: samples.length ? union(samples.map((s) => inferSchema(s))) : { type: "object" },
      shape: samples.length ? "sample" : "unknown",
    });
  }
  return out;
}

function replyButtons(n: SpecNode): { action: { type: string; wait?: string } }[] {
  return (n.reply?.blocks ?? []).flatMap((b) => (b.kind === "quick_replies" ? b.options : b.kind === "buttons" ? b.buttons : b.kind === "cards" ? (b.buttons ?? []) : [])) as { action: { type: string; wait?: string } }[];
}

const ROWS: Schema = { type: "array", items: { type: "object" }, "x-label": { en: "Rows", ar: "الصفوف" } };

/** What a table step makes (ADR-0105; the SDK's `table_schema`). */
export function tableSchema(n: SpecNode): Schema {
  const lab = (en: string, ar: string) => ({ "x-label": { en, ar } });
  if (n.op === "match") {
    return {
      type: "object",
      properties: {
        matched: { type: "array", items: { type: "object", properties: { left: { type: "object" }, right: { type: "object" }, differences: { type: "object" } } }, ...lab("Matched pairs", "الأزواج المتطابقة") },
        unmatched_left: { ...ROWS, ...lab("Left over in the first table", "المتبقي في الجدول الأول") },
        unmatched_right: { ...ROWS, ...lab("Left over in the second table", "المتبقي في الجدول الثاني") },
        counts: { type: "object", properties: { matched: { type: "integer" }, unmatched_left: { type: "integer" }, unmatched_right: { type: "integer" } }, ...lab("How many", "الأعداد") },
      },
    };
  }
  if (n.op === "group") {
    const props: Record<string, Schema> = Object.fromEntries((n.by ?? []).map((b) => [b, {}]));
    for (const m of n.measures ?? []) props[m.name] = { type: m.fn === "count" ? "integer" : "number" };
    return { type: "array", items: { type: "object", properties: props } };
  }
  return { type: "object", properties: { name: { type: "string" }, columns: { type: "array", items: { type: "string" } }, rows: ROWS, row_count: { type: "integer", ...lab("How many rows", "عدد الصفوف") }, truncated: { type: "boolean" } } };
}

/** A for-each step's results (ADR-0104): one entry per item, in the list's order. */
export function foreachSchema(item: Schema, made: Record<string, Schema>): Schema {
  return {
    type: "array",
    items: {
      type: "object",
      properties: {
        item,
        status: { type: "string", enum: ["done", "failed", "skipped", "not_run"], "x-label": { en: "How it went", ar: "كيف سار" } },
        result: Object.keys(made).length ? { type: "object", properties: made } : {},
        error: { type: ["string", "null"], "x-label": { en: "Why it failed", ar: "سبب الفشل" } },
      },
    },
  };
}

/** The item each for-each step works on, and the shape of its results (ADR-0104). */
function loopValues(spec: AgentSpec, values: DataValue[], tools: readonly RegistryTool[]): DataValue[] {
  const out: DataValue[] = [];
  for (const n of spec.spec.graph.nodes) {
    if (n.type !== "foreach") continue;
    const segs = pathSegments(n.items ?? "");
    let s: Schema | undefined = union(values.filter((x) => x.key === segs[0]).map((x) => x.schema));
    for (const seg of segs.slice(1)) s = s ? (childSchema(s, seg) ?? undefined) : undefined;
    const item = (s ? childSchema(s, 0) : undefined) ?? {};
    const made: Record<string, Schema> = {};
    for (const id of n.steps ?? []) {
      const step = spec.spec.graph.nodes.find((x) => x.id === id);
      for (const w of step ? writes(step, spec, tools) : []) if (w.key !== "messages") made[w.key] = w.schema;
    }
    const key = typeof n.output_key === "string" && n.output_key ? n.output_key : `${n.id}_results`;
    const i = values.findIndex((x) => x.node === n.id && x.key === key);
    if (i >= 0) values[i] = { ...(values[i] as DataValue), schema: foreachSchema(item, n.collect ? {} : made) };
    const label = labelOf(n, "loop_item");
    out.push(
      { key: n.item_key || "item", node: n.id, kind: "loop_item", label, schema: item, shape: "platform" },
      { key: "item_number", node: n.id, kind: "loop_item", label: { en: "Which item this is (1, 2, …)", ar: "رقم العنصر (1، 2، …)" }, schema: { type: "integer" }, shape: "platform" },
      { key: "item_count", node: n.id, kind: "loop_item", label: { en: "How many items there are", ar: "عدد العناصر" }, schema: { type: "integer" }, shape: "platform" },
    );
  }
  return out;
}

/** Successors as the SDK's `Graph.successors` (explicit and implied edges). */
function successors(spec: AgentSpec): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const add = (a: string, b: string) => out.set(a, [...(out.get(a) ?? []), b]);
  for (const e of spec.spec.graph.edges ?? []) add(e.from, e.to);
  for (const n of spec.spec.graph.nodes) {
    if (n.type === "router") [...(n.routes ?? []).map((r) => r.to), ...(n.default ? [n.default] : [])].forEach((t) => add(n.id, t));
    else if (n.type === "parallel") (n.branches ?? []).forEach((b) => add(n.id, b));
    else if (n.type === "human_approval" && n.on_reject) add(n.id, n.on_reject);
    else if (n.type === "decision" && n.on_low_confidence?.route) add(n.id, n.on_low_confidence.route);
    if (n.on_limit) add(n.id, n.on_limit);
    if (n.type === "tool" && typeof n.on_error === "string" && n.on_error && n.on_error !== "fail" && n.on_error !== "continue") add(n.id, n.on_error);
    if (n.type === "wait" && n.on_give_up) add(n.id, n.on_give_up);
    const first = n.type === "foreach" ? n.steps?.[0] : undefined;
    if (first) add(n.id, first);
    for (const b of n.type === "llm" ? replyButtons(n) : []) if (b.action.type === "handoff" && b.action.wait) add(n.id, b.action.wait);
  }
  return out;
}

/** The data map computed in the browser (values and availability; the checks are the server's). */
export function localDataMap(spec: AgentSpec, tools: readonly RegistryTool[] = []): DataMap {
  const start = startValues(spec);
  const values = [...start];
  const writesOf = new Map<string, Set<string>>();
  for (const n of spec.spec.graph.nodes) {
    const w = writes(n, spec, tools);
    writesOf.set(n.id, new Set(w.map((x) => x.key)));
    values.push(...w);
  }
  const loop = loopValues(spec, values, tools);
  values.push(...loop);
  const startAlways = new Set(["question", "lang", ...start.filter((v) => v.key === "visits").map((v) => v.key)]);
  const startAll = new Set(start.map((v) => v.key));
  const succ = successors(spec);
  const entry = spec.spec.graph.entry;
  const ids = new Set(spec.spec.graph.nodes.map((n) => n.id));
  const reachable = new Set<string>();
  const stack = [entry];
  while (stack.length) {
    const cur = stack.pop() as string;
    if (reachable.has(cur) || !ids.has(cur)) continue;
    reachable.add(cur);
    stack.push(...(succ.get(cur) ?? []));
  }
  const preds = new Map<string, string[]>([...reachable].map((n) => [n, []]));
  for (const a of reachable) for (const b of succ.get(a) ?? []) if (preds.has(b) && !preds.get(b)?.includes(a)) preds.get(b)?.push(a);
  const everything = new Set([...startAll, ...[...writesOf.values()].flatMap((s) => [...s])]);
  const mustOut = new Map([...reachable].map((n) => [n, new Set(everything)]));
  const mayOut = new Map([...reachable].map((n) => [n, new Set<string>()]));
  const mustIn = new Map<string, Set<string>>();
  const mayIn = new Map<string, Set<string>>();
  const same = (a: Set<string> | undefined, b: Set<string>) => !!a && a.size === b.size && [...a].every((x) => b.has(x));
  for (let changed = true, guard = 0; changed && guard < 200; guard++) {
    changed = false;
    for (const n of reachable) {
      const ps = preds.get(n) ?? [];
      let mi: Set<string>;
      if (n === entry || !ps.length) mi = new Set(startAlways);
      else {
        const sets = ps.map((p) => mustOut.get(p) as Set<string>);
        mi = new Set([...(sets[0] as Set<string>)].filter((k) => sets.every((s) => s.has(k))));
      }
      const ma = new Set([...startAll, ...ps.flatMap((p) => [...(mayOut.get(p) as Set<string>)])]);
      const own = writesOf.get(n) ?? new Set<string>();
      const mo = new Set([...mi, ...own]);
      const mao = new Set([...ma, ...own]);
      if (!same(mustIn.get(n), mi) || !same(mayIn.get(n), ma) || !same(mustOut.get(n), mo) || !same(mayOut.get(n), mao)) {
        mustIn.set(n, mi);
        mayIn.set(n, ma);
        mustOut.set(n, mo);
        mayOut.set(n, mao);
        changed = true;
      }
    }
  }
  const view = (must: Set<string>, may: Set<string>) =>
    Object.fromEntries([...new Set([...must, ...may])].sort().map((k) => [k, must.has(k) ? ("always" as const) : ("maybe" as const)]));
  const before: DataMap["before"] = {};
  const after: DataMap["after"] = {};
  for (const n of reachable) {
    before[n] = view(mustIn.get(n) ?? new Set(), mayIn.get(n) ?? new Set());
    after[n] = view(mustOut.get(n) ?? new Set(), mayOut.get(n) ?? new Set());
  }
  // Inside a for-each step's steps the item is always there; nowhere else (ADR-0104).
  for (const n of spec.spec.graph.nodes) {
    if (n.type !== "foreach") continue;
    const keys = loop.filter((x) => x.node === n.id).map((x) => x.key);
    for (const s of n.steps ?? [])
      for (const view of [before, after]) if (view[s]) view[s] = { ...view[s], ...Object.fromEntries(keys.map((k) => [k, "always" as const])) };
  }
  const map: DataMap = { values, before, after, inputs: ["question", "lang"], subagent_inputs: {}, issues: [] };
  return { ...map, issues: localChecks(spec, map, tools) };
}

// ---------------------------------------------------------------- the checks the browser makes at once

/**
 * Data checks the console makes itself, at once (fix wave 2): the same codes and words as the
 * SDK's (`agent_platform_sdk.spec.datamap`), so a new step's "Checks" line is honest before the
 * server answers, and an invalid draft (which the server can't map) still shows them.
 */
export const LOCAL_CHECKS: ReadonlySet<string> = new Set(["bad_template", "prompt_placeholder", "prompt_unknown", "missing_arg", "decision_no_path", "decision_plain_arrow", "no_answer", "tool_not_connected"]);

/**
 * Checks only the console makes (the control plane's map doesn't): they are kept next to the
 * server's checks when it answers. `tool_unused`: an AI step offered a system no step runs
 * (G-camp-ops-09); `file_level_low`: a file made below the agent's data level (G-camp-ops-22).
 */
export const BROWSER_CHECKS: ReadonlySet<string> = new Set(["tool_unused", "file_level_low"]);

/** Whether a server issue is one the browser also works out (it is replaced by the local one). */
export function isLocalCheck(i: Pick<DataIssue, "code" | "where">): boolean {
  return LOCAL_CHECKS.has(i.code) || i.where.startsWith("prompt.");
}

/**
 * The checks to show when the server's map is fresh: the server's, without what the console
 * knows better (a "still needs" warning on a step that passes on what the AI wrote, from an older
 * control plane; "no arrow carries out the call" where the console names each unused system),
 * and the checks only the console makes.
 */
export function mergeIssues(spec: AgentSpec, server: readonly DataIssue[], local: readonly DataIssue[]): DataIssue[] {
  const byId = new Map(spec.spec.graph.nodes.map((n) => [n.id, n]));
  const unused = new Set(local.filter((i) => i.code === "tool_unused").map((i) => i.node));
  const kept = server.filter((i) => {
    const n = i.node ? byId.get(i.node) : undefined;
    if (i.code === "missing_arg" && n && passesAiCall(n)) return false;
    if (i.code === "tool_call_unrouted" && unused.has(i.node)) return false;
    return !BROWSER_CHECKS.has(i.code);
  });
  return [...kept, ...local.filter((i) => BROWSER_CHECKS.has(i.code))];
}

/** The guided builder's pass-through: `{"input": "{{ state.tool_call.args }}"}` (the orchestrator's `tool_args`). */
const PASS_THROUGH = "{{ state.tool_call.args }}";

/**
 * Whether a "Use a system" step sends what an AI step wrote as its whole set of values (the
 * ReAct "Act" step, `$args` or the guided builder's `input`): the AI fills the system's values at
 * run time, so none is missing in the step itself (G-camp-fin-19, G-camp-ops-17).
 */
export function passesAiCall(n: SpecNode): boolean {
  if (n.type !== "tool" || !isObj(n.args)) return false;
  const keys = Object.keys(n.args);
  if (keys.some((k) => k.startsWith("$"))) return true;
  return keys.length === 1 && keys[0] === "input" && typeof n.args.input === "string" && n.args.input.trim() === PASS_THROUGH;
}

/**
 * A field's name for people: its schema title, unless the title is a technical type name
 * (`PaymentIn`, the class a nested value was made from), then the field's own name in words.
 */
export function plainFieldTitle(name: string, prop: unknown): string {
  const title = isObj(prop) && typeof prop.title === "string" ? prop.title.trim() : "";
  if (!title) return humaniseName(name);
  const typeName = !/\s/.test(title) && (/[a-z0-9][A-Z]/.test(title) || (isObj(prop) && (prop.type === "object" || isObj(prop.properties) || typeof prop.$ref === "string")));
  return typeName ? humaniseName(name) : title;
}

const LEVELS = ["public", "internal", "confidential", "restricted"] as const;
const LEVEL_WORDS: Record<string, Text> = {
  public: { en: "Public", ar: "عامة" },
  internal: { en: "Internal", ar: "داخلية" },
  confidential: { en: "Confidential", ar: "سرية" },
  restricted: { en: "Restricted", ar: "مقيّدة" },
};
const levelRankOf = (l: string): number => LEVELS.indexOf(l as (typeof LEVELS)[number]);

/** The plain name of a system (from the catalogue), in both languages. */
function systemText(ref: string, tools: readonly RegistryTool[]): Text {
  const t = tools.find((x) => x.ref === ref);
  return t ? { en: t.name.en || t.name.ar, ar: t.name.ar || t.name.en } : { en: humaniseName(ref), ar: humaniseName(ref) };
}

/**
 * An AI step offered a system that no step after it runs (G-camp-ops-09): the AI may ask for it,
 * but nothing carries the call out, so the agent can't actually use it (and may say it did).
 */
function unusedSystemChecks(spec: AgentSpec, add: (i: Omit<DataIssue, "path" | "suggestion"> & { path?: string | null; suggestion?: string | null }) => void, tools: readonly RegistryTool[]): void {
  const succ = successors(spec);
  const byId = new Map(spec.spec.graph.nodes.map((n) => [n.id, n]));
  for (const n of spec.spec.graph.nodes) {
    if (n.type !== "llm" || !Array.isArray(n.tools) || !n.tools.length) continue;
    const seen = new Set<string>();
    const stack = [...(succ.get(n.id) ?? [])];
    while (stack.length) {
      const cur = stack.pop() as string;
      if (seen.has(cur)) continue;
      seen.add(cur);
      stack.push(...(succ.get(cur) ?? []));
    }
    const run = new Set([...seen].map((id) => byId.get(id)).filter((x): x is SpecNode => x?.type === "tool").map((x) => String(x.tool ?? "")));
    for (const ref of n.tools as string[]) {
      if (run.has(ref)) continue;
      add({
        code: "tool_unused",
        severity: "warning",
        node: n.id,
        where: "tools",
        path: ref,
        message: tr(
          "Step ‘{s}’ may use ‘{t}’, but no step runs it, so the agent can't actually use it. Add a ‘Use a system’ step for it after ‘{s}’, or stop offering it to this step.",
          "يمكن للخطوة ‘{s}’ استخدام ‘{t}’، لكن لا توجد خطوة تشغّله، لذا لا يستطيع الوكيل استخدامه فعلاً. أضف بعدها خطوة ‘استخدام نظام’ له، أو توقّف عن إتاحته لهذه الخطوة.",
          { s: stepText(n), t: systemText(ref, tools) },
        ),
      });
    }
  }
}

/**
 * A step that makes a file (a PDF, a spreadsheet, a download) at a data level below the agent's
 * (G-camp-ops-22): the platform refuses a file below the level of the data the agent handles.
 */
function fileLevelChecks(spec: AgentSpec, add: (i: Omit<DataIssue, "path" | "suggestion"> & { path?: string | null; suggestion?: string | null }) => void, tools: readonly RegistryTool[]): void {
  const agent = String(spec.spec.permissions?.max_classification ?? "internal");
  if (levelRankOf(agent) < 0) return;
  for (const n of spec.spec.graph.nodes) {
    if (n.type !== "tool" || passesAiCall(n) || !isObj(n.args)) continue;
    const schema = tools.find((t) => t.ref === n.tool)?.actions.find((a) => a.name === n.action)?.input_schema as Schema | undefined;
    const prop = schema && isObj(schema.properties) ? (schema.properties as Record<string, Schema>).data_level : undefined;
    if (!isObj(prop)) continue;
    const given = n.args.data_level;
    if (typeof given === "string" && given.includes("{{")) continue; // set from data at run time
    const level = typeof given === "string" && given ? given : typeof prop.default === "string" ? prop.default : "internal";
    if (levelRankOf(level) < 0 || levelRankOf(level) >= levelRankOf(agent)) continue;
    add({
      code: "file_level_low",
      severity: "warning",
      node: n.id,
      where: "args.data_level",
      path: "data_level",
      suggestion: agent,
      message: tr(
        "Step ‘{s}’ makes a file at the {level} data level, but this agent handles {agent} data, so the file is refused when the step runs. Set the step's data level to {agent}.",
        "الخطوة ‘{s}’ تنشئ ملفاً بمستوى بيانات {level}، لكن هذا الوكيل يتعامل مع بيانات {agent}، لذا يُرفض الملف عند تشغيل الخطوة. اضبط مستوى بيانات الخطوة على {agent}.",
        { s: stepText(n), level: LEVEL_WORDS[level] ?? { en: level, ar: level }, agent: LEVEL_WORDS[agent] ?? { en: agent, ar: agent } },
      ),
    });
  }
}

const PROMPT_BUILT_IN = new Set(["attachments", "today", "weekday", "now"]);
const PLACEHOLDER = /(?<!\{)\{([A-Za-z_]\w*)((?:\.[A-Za-z_]\w*|\[\w+\])*)\}(?!\})/g;

function tr(en: string, ar: string, params: Record<string, string | Text>): Text {
  const fill = (tpl: string, lang: "en" | "ar") => tpl.replace(/\{(\w+)\}/g, (m, k: string) => {
    const v = params[k];
    return v === undefined ? m : typeof v === "string" ? v : v[lang];
  });
  return { en: fill(en, "en"), ar: fill(ar, "ar") };
}

function stepText(n: SpecNode | undefined, fallback = ""): Text {
  if (!n) return { en: humaniseName(fallback), ar: humaniseName(fallback) };
  if (n.label && (n.label.en || n.label.ar)) return { en: n.label.en || n.label.ar, ar: n.label.ar || n.label.en };
  if (n.type === "end") return { en: "Finish", ar: "النهاية" };
  const h = humaniseName(n.id);
  return { en: h, ar: h };
}

/** As the SDK's `_close`: a near spelling, else the one name that contains the word. */
function closeName(word: string, names: readonly string[]): string | null {
  const w = word.toLowerCase();
  let best: string | null = null;
  let score = 0.6;
  for (const n of names) {
    const r = similarity(w, n.toLowerCase());
    if (r >= score) {
      best = n;
      score = r;
    }
  }
  if (best) return best;
  if (word.length >= 3) {
    const within = names.filter((n) => n.toLowerCase().includes(w));
    if (within.length === 1) return within[0] as string;
  }
  return null;
}

/** A difflib-like ratio (2 × matching characters / total), enough for "did you mean". */
function similarity(a: string, b: string): number {
  if (!a.length && !b.length) return 1;
  const match = (x: string, y: string): number => {
    if (!x || !y) return 0;
    let bi = 0, bj = 0, bl = 0;
    for (let i = 0; i < x.length; i++)
      for (let j = 0; j < y.length; j++) {
        let k = 0;
        while (i + k < x.length && j + k < y.length && x[i + k] === y[j + k]) k++;
        if (k > bl) [bi, bj, bl] = [i, j, k];
      }
    return bl ? bl + match(x.slice(0, bi), y.slice(0, bj)) + match(x.slice(bi + bl), y.slice(bj + bl)) : 0;
  };
  return (2 * match(a, b)) / (a.length + b.length);
}

const hintText = (name: string | null): Text => (name ? { en: ` Did you mean ‘${name}’?`, ar: ` هل تقصد ‘${name}’؟` } : { en: "", ar: "" });

const YES_NO_VALUES: { value: string; label: Text }[] = [
  { value: "yes", label: { en: "Yes", ar: "نعم" } },
  { value: "no", label: { en: "No", ar: "لا" } },
];

/** Whether a step gives the person an answer (an AI step, a sub-agent, one that writes `answer`, or a hand-over to a person, who answers from then on). */
function answers(n: SpecNode): boolean {
  return n.type === "llm" || n.type === "subgraph" || n.type === "handover" || n.output_key === "answer";
}

// As the SDK's `_SEARCH_WORDS`: a system named like a search most likely wants a "Search
// knowledge" step (the platform's own document search) instead.
const SEARCH_WORDS = ["search", "knowledge", "retriev", "lookup"];
const EMPTY_TEXT: Text = { en: "", ar: "" };

/** Whether a system name (or its actions) reads like a document search. */
export function searchLike(ref: string, actions: readonly string[] = []): boolean {
  return [ref, ...actions].some((x) => SEARCH_WORDS.some((w) => x.toLowerCase().includes(w)));
}

/**
 * Systems a step uses (a tool step) or offers the AI (an AI step's systems) that the
 * organisation's catalogue doesn't have, or actions that system doesn't offer: the SDK's
 * `tool_not_connected`, in the same words. Nothing is said before the catalogue has loaded.
 */
function connectedChecks(spec: AgentSpec, tools: readonly RegistryTool[], add: (i: Omit<DataIssue, "path" | "suggestion"> & { path?: string | null; suggestion?: string | null }) => void): void {
  if (!tools.length) return;
  const grants = new Map((spec.spec.tools ?? []).map((g) => [g.ref, g.actions ?? []]));
  for (const n of spec.spec.graph.nodes) {
    let uses: [string, string[], string][] = [];
    if (n.type === "tool" && typeof n.tool === "string" && n.tool) uses = [[n.tool, typeof n.action === "string" && n.action ? [n.action] : [], "tool"]];
    else if (n.type === "llm" && Array.isArray(n.tools)) uses = (n.tools as string[]).map((ref) => [ref, grants.get(ref) ?? [], "tools"]);
    for (const [ref, actions, where] of uses) {
      const reg = tools.find((t) => t.ref === ref);
      const s = stepText(n);
      if (!reg) {
        const hint = searchLike(ref, actions) ? { en: " To look something up in your documents, use a ‘Search knowledge’ step instead.", ar: " للبحث في مستنداتك، استخدم خطوة ‘بحث في المعرفة’ بدلاً منها." } : EMPTY_TEXT;
        add({
          code: "tool_not_connected",
          severity: "error",
          node: n.id,
          where,
          path: ref,
          message:
            n.type === "llm"
              ? tr(
                  "Step ‘{s}’ uses ‘{t}’, which isn't connected in your organisation, so the AI can't use it. Remove it from the systems the AI may use, or pick a connected system.{hint}",
                  "الخطوة ‘{s}’ تستخدم ‘{t}’، وهو غير متصل في مؤسستك، لذا لا يستطيع الذكاء الاصطناعي استخدامه. أزله من الأنظمة التي يمكن للذكاء الاصطناعي استخدامها، أو اختر نظاماً متصلاً.{hint}",
                  { s, t: ref, hint },
                )
              : tr("Step ‘{s}’ uses ‘{t}’, which isn't connected in your organisation, so it can't run. Pick a connected system in the step.{hint}", "الخطوة ‘{s}’ تستخدم ‘{t}’، وهو غير متصل في مؤسستك، لذا لا يمكن تشغيلها. اختر نظاماً متصلاً في الخطوة.{hint}", { s, t: ref, hint }),
        });
        continue;
      }
      const offered = reg.actions.map((a) => a.name);
      for (const action of actions) {
        if (offered.includes(action)) continue;
        add({
          code: "tool_not_connected",
          severity: "error",
          node: n.id,
          where: where === "tool" ? "action" : where,
          path: `${ref}.${action}`,
          suggestion: closeName(action, offered),
          message: tr("Step ‘{s}’ uses ‘{a}’ of ‘{t}’, which that system doesn't offer, so it can't run. Pick one of its actions.{hint}", "الخطوة ‘{s}’ تستخدم ‘{a}’ من ‘{t}’، وهذا النظام لا يقدّمه، لذا لا يمكن تشغيلها. اختر أحد إجراءاته.{hint}", {
            s,
            a: action,
            t: ref,
            hint: offered.length ? { en: ` Its actions: ${offered.join(", ")}.`, ar: ` إجراءاته: ${offered.join(", ")}.` } : EMPTY_TEXT,
          }),
        });
      }
    }
  }
}

export function localChecks(spec: AgentSpec, dm: Pick<DataMap, "values" | "before">, tools: readonly RegistryTool[] = []): DataIssue[] {
  const out: DataIssue[] = [];
  const nodes = spec.spec.graph.nodes;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const keys = [...new Set(dm.values.map((v) => v.key))];
  const add = (i: Omit<DataIssue, "path" | "suggestion"> & { path?: string | null; suggestion?: string | null }) => {
    if (out.some((x) => x.code === i.code && x.node === i.node && x.where === i.where && x.path === (i.path ?? null))) return;
    out.push({ path: null, suggestion: null, ...i });
  };
  connectedChecks(spec, tools, add);
  unusedSystemChecks(spec, add, tools);
  if (tools.length) fileLevelChecks(spec, add, tools);
  for (const n of nodes) {
    const subject = stepText(n);
    // Prompt placeholders (G-io-03, G-io-10).
    const tpl = isObj(n.prompt) && typeof n.prompt.template === "string" ? n.prompt.template : "";
    if (n.type === "llm" && tpl) {
      const vars = isObj(n.prompt) && Array.isArray(n.prompt.variables) ? (n.prompt.variables as string[]) : [];
      const given = new Set(vars.map((v) => v.split(/[.[]/)[0] as string));
      for (const m of tpl.matchAll(PLACEHOLDER)) {
        const root = m[1] as string;
        const rest = m[2] ?? "";
        if (PROMPT_BUILT_IN.has(root)) continue;
        const known = keys.includes(root);
        // A value the agent has but the step doesn't list is filled at run time by its name too
        // (G-camp-cs-22), so only a name nothing makes is a problem.
        if (!given.has(root) && !known) {
          const close = closeName(root, keys);
          add({
            code: "prompt_unknown",
            severity: "warning",
            node: n.id,
            where: "prompt.template",
            path: root,
            suggestion: close,
            message: tr("Step ‘{s}’: the prompt uses {{name}}, but no earlier step makes ‘{name}’, so the AI gets it as written.{hint}", "الخطوة ‘{s}’: يستخدم النص {{name}}، لكن لا توجد خطوة سابقة تنتج ‘{name}’، لذا يصل إلى الذكاء الاصطناعي كما هو مكتوب.{hint}", { s: subject, name: root, hint: hintText(close) }),
          });
          continue;
        }
        if (!known) {
          const close = closeName(root, keys);
          add({
            code: "unknown_value",
            severity: "warning",
            node: n.id,
            where: "prompt.variables",
            path: root,
            suggestion: close,
            message: tr(
              "{subject} uses ‘{path}’, which no step makes and the request doesn't include. It is empty unless the request or the event that starts the agent sends it.{hint}",
              "{subject} تستخدم ‘{path}’، ولا تنتجها أي خطوة ولا يتضمنها الطلب. ستكون فارغة ما لم يرسلها الطلب أو الحدث الذي يبدأ الوكيل.{hint}",
              { subject: tr("Step ‘{s}’", "الخطوة ‘{s}’", { s: subject }), path: root, hint: hintText(close) },
            ),
          });
          continue;
        }
        if (!rest) continue;
        // A field inside the value: only a shape with listed fields can say it is missing.
        let schema: Schema | null = union(dm.values.filter((v) => v.key === root).map((v) => v.schema));
        let path = root;
        for (const seg of pathSegments(rest.replace(/\[([A-Za-z_]\w*)\]/g, ".$1"))) {
          const props = schema && isObj(schema.properties) ? (schema.properties as Record<string, Schema>) : null;
          if (typeof seg === "string" && props && !(seg in props) && !isObj(schema?.additionalProperties)) {
            const names = Object.keys(props);
            const close = closeName(seg, names);
            add({
              code: "unknown_field",
              severity: "warning",
              node: n.id,
              where: "prompt.template",
              path: `${path}.${seg}`,
              suggestion: close ? `${path}.${close}` : null,
              message: tr("{subject} uses ‘{field}’ of ‘{value}’, which has no such field.{hint}", "{subject} تستخدم الحقل ‘{field}’ من ‘{value}’، ولا يوجد فيها حقل بهذا الاسم.{hint}", {
                subject: tr("Step ‘{s}’", "الخطوة ‘{s}’", { s: subject }),
                field: `${path.split(".").slice(1).concat(seg).join(".")}`,
                value: dm.values.find((v) => v.key === root)?.label ?? { en: root, ar: root },
                hint: close ? hintText(close) : names.length && names.length <= 12 ? { en: ` Its fields: ${names.join(", ")}.`, ar: ` حقولها: ${names.join("، ")}.` } : { en: "", ar: "" },
              }),
            });
            break;
          }
          schema = childSchema(schema, seg);
          path = typeof seg === "number" ? `${path}[${seg}]` : `${path}.${seg}`;
          if (!schema) break;
        }
      }
    }
    // A value the system needs that isn't set (G-kn-12). A step that passes on what the AI wrote
    // gets its values at run time (G-camp-fin-19).
    if (n.type === "tool" && isObj(n.args) && !passesAiCall(n)) {
      const schema = tools.find((t) => t.ref === n.tool)?.actions.find((a) => a.name === n.action)?.input_schema;
      for (const name of schema?.required ?? []) {
        const v = (n.args as Record<string, unknown>)[name];
        if (v !== undefined && v !== null && v !== "") continue;
        const title = plainFieldTitle(name, schema?.properties?.[name]);
        add({
          code: "missing_arg",
          severity: "warning",
          node: n.id,
          where: `args.${name}`,
          path: name,
          message: tr("Step ‘{s}’ still needs ‘{field}’: without it the system refuses the call. Set it in the step.", "الخطوة ‘{s}’ ما زالت تحتاج إلى ‘{field}’: من دونها يرفض النظام الطلب. اضبطها في الخطوة.", { s: subject, field: title }),
        });
      }
    }
    // Each answer of an AI choice needs its own path (G-flow-05).
    if (n.type === "decision" && n.kind !== "score") {
      const edges = (spec.spec.graph.edges ?? []).filter((e) => e.from === n.id);
      const plain = edges.filter((e) => !e.when);
      for (const e of plain)
        add({
          code: "decision_plain_arrow",
          severity: "warning",
          node: n.id,
          where: `edge ${e.from}->${e.to}`,
          message: tr("The arrow from ‘{a}’ to ‘{b}’ isn't for one answer, so the run always takes it, whatever is chosen. Pick the answer it is for.", "السهم من ‘{a}’ إلى ‘{b}’ ليس لإجابة واحدة، لذا يسلكه التشغيل دائماً مهما كان الاختيار. اختر الإجابة التي هو لها.", { a: subject, b: stepText(byId.get(e.to), e.to) }),
        });
      if (!plain.length) {
        const options = n.kind === "boolean" ? (n.options?.length ? n.options : YES_NO_VALUES) : (n.options ?? []);
        for (const o of options) {
          if (edges.some((e) => e.when === o.value)) continue;
          add({
            code: "decision_no_path",
            severity: "warning",
            node: n.id,
            where: `options.${o.value}`,
            path: o.value,
            message: tr("‘{s}’: the answer ‘{option}’ has no path, so the run stops there when it is chosen. Add a step for it, or point an arrow at the next step.", "‘{s}’: الإجابة ‘{option}’ ليس لها مسار، لذا يتوقف التشغيل عندها إذا اختيرت. أضف خطوة لها، أو وجّه سهماً إلى الخطوة التالية.", {
              s: subject,
              option: o.label && (o.label.en || o.label.ar) ? { en: o.label.en || o.label.ar, ar: o.label.ar || o.label.en } : { en: o.value, ar: o.value },
            }),
          });
        }
      }
    }
  }
  // A chat run that can reach Finish with no step that answers the person (G-kn-13).
  if (!spec.spec.permissions?.autonomous) {
    const succ = successors(spec);
    const seen = new Set<string>();
    const stack = [spec.spec.graph.entry];
    while (stack.length) {
      const cur = stack.pop() as string;
      const n = byId.get(cur);
      if (seen.has(cur) || !n || answers(n)) continue;
      seen.add(cur);
      if (n.type === "end") {
        const last = [...seen].filter((a) => (succ.get(a) ?? []).includes(cur)).sort()[0];
        const before = last ? byId.get(last) : undefined;
        add({
          code: "no_answer",
          severity: "warning",
          node: (before ?? n).id,
          where: "answer",
          message: before
            ? tr("The run can reach ‘{end}’ after ‘{s}’ without any step that answers the person, so they get no reply. Add an ‘Ask the AI’ step that answers before it.", "يمكن أن يصل التشغيل إلى ‘{end}’ بعد ‘{s}’ دون أي خطوة تجيب الشخص، فلا يصله رد. أضف خطوة ‘اسأل الذكاء الاصطناعي’ تجيب قبلها.", { end: stepText(n), s: stepText(before) })
            : // Finish is the first step (a new blank agent): nothing comes "before" it.
              tr("The run goes straight to ‘{end}’, so the person gets no reply. Add an ‘Ask the AI’ step that answers before it.", "يذهب التشغيل مباشرة إلى ‘{end}’، فلا يصل الشخص رد. أضف خطوة ‘اسأل الذكاء الاصطناعي’ تجيب قبلها.", { end: stepText(n) }),
        });
        break;
      }
      stack.push(...(succ.get(cur) ?? []));
    }
  }
  // Values typed as `{{x}}` in a step (G-camp-cs-14): the platform reads `{{ state.x }}`.
  for (const n of nodes) {
    const where: [string, unknown][] = n.type === "tool" ? [["args", n.args]] : n.type === "decision" ? [["input", n.input]] : n.type === "retrieval" ? [["query", n.query]] : n.type === "subgraph" ? [["input_map", n.input_map]] : [];
    for (const [at, value] of where) {
      for (const bad of badTemplates(value, keys)) {
        add({
          code: "bad_template",
          severity: "error",
          node: n.id,
          where: at,
          path: bad.ref,
          suggestion: bad.suggestion,
          message: bad.suggestion
            ? tr("Step ‘{s}’ uses {ref}, which the platform can't read. Write {fix} instead, or pick it from “Value from an earlier step”.", "الخطوة ‘{s}’ تستخدم {ref}، ولا تستطيع المنصة قراءتها. اكتب {fix} بدلًا منها، أو اخترها من “قيمة من خطوة سابقة”.", { s: stepText(n), ref: bad.ref, fix: `{{ ${bad.suggestion} }}` })
            : tr("Step ‘{s}’ uses {ref}, which isn't a value the agent has, so the step can't be saved. Pick the value from “Value from an earlier step”, or remove the braces.", "الخطوة ‘{s}’ تستخدم {ref}، وهي ليست قيمة لدى الوكيل، لذا لا يمكن حفظ الخطوة. اختر القيمة من “قيمة من خطوة سابقة”، أو احذف الأقواس.", { s: stepText(n), ref: bad.ref }),
        });
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------- fields for intellisense

export interface DataField {
  /** State path without `state.`, e.g. `request.pages` or `search.clubs[0].name`. */
  path: string;
  /** The value it belongs to (`request`). */
  root: string;
  /** The value's plain name ("The request"). */
  valueLabel: string;
  /** The field inside the value in plain words ("Pages to open"); null for the whole value. */
  fieldLabel: string | null;
  types: string[] | null;
  description: string | null;
  /** Values it can take, when the shape lists them (enum). */
  known: unknown[] | null;
  availability: Availability;
  /** The step that makes it (null for start values) and its name. */
  step: string | null;
  stepLabel: string | null;
  depth: number;
  schema: Schema;
}

/** Where in the agent: before a step runs, or after it (on the arrows leaving it). */
export type DataPoint = { before: string } | { after: string } | { end: true } | null;

function availableAt(dm: DataMap, spec: AgentSpec, at: DataPoint): Record<string, Availability> | null {
  if (!at) return null;
  if ("before" in at) return dm.before[at.before] ?? {};
  if ("after" in at) return dm.after[at.after] ?? {};
  // At the end: what any end step can see.
  const ends = spec.spec.graph.nodes.filter((n) => n.type === "end").map((n) => n.id);
  const out: Record<string, Availability> = {};
  for (const e of ends) for (const [k, a] of Object.entries(dm.before[e] ?? {})) out[k] = out[k] === "always" || a === "always" ? "always" : "maybe";
  return out;
}

const pathOf = (base: string, seg: string | number) => (typeof seg === "number" ? `${base}[${seg}]` : `${base}.${seg}`);

/**
 * Every field the agent offers at a point, as a flat list for pickers: each value, then its
 * fields (and the fields of a list's items as `[0]`) down to `maxDepth`. `at: null` lists all.
 */
export function dataFields(dm: DataMap, spec: AgentSpec, at: DataPoint, lang: "ar" | "en", opts: { maxDepth?: number; messages?: boolean } = {}): DataField[] {
  const maxDepth = opts.maxDepth ?? 3;
  const avail = availableAt(dm, spec, at);
  const byKey = new Map<string, DataValue[]>();
  for (const v of dm.values) byKey.set(v.key, [...(byKey.get(v.key) ?? []), v]);
  const out: DataField[] = [];
  for (const [key, vs] of byKey) {
    if (key === "messages" && !opts.messages) continue;
    const a = avail ? avail[key] : "maybe";
    if (!a) continue;
    const first = vs[0] as DataValue;
    const schema = union(vs.map((v) => v.schema));
    const step = first.node;
    const node = step ? spec.spec.graph.nodes.find((n) => n.id === step) : undefined;
    const valueLabel = first.label[lang] || first.label.en;
    const base = { root: key, valueLabel, availability: a, step, stepLabel: node ? nodeName(node, lang) : null };
    const describe = (s: Schema): string | null => (typeof s.description === "string" ? s.description : null);
    out.push({ ...base, path: key, fieldLabel: null, types: schemaTypes(schema), description: describe(schema), known: knownValues(schema), depth: 0, schema });
    if (key === "visits") {
      // One field per step with a repeat limit: "How many times ‘Critique’ has run" (state.visits.critique).
      const count: Schema = { type: "integer", minimum: 0 };
      for (const n of spec.spec.graph.nodes.filter((x) => x.max_iterations)) {
        const name = nodeName(n, lang);
        out.push({ ...base, path: pathOf(key, n.id), valueLabel: lang === "ar" ? `عدد مرات تشغيل ‹${name}›` : `How many times ‘${name}’ has run`, fieldLabel: null, types: ["integer"], description: null, known: null, depth: 1, schema: count });
      }
      continue;
    }
    const walk = (s: Schema, path: string, label: string[], depth: number) => {
      if (depth > maxDepth) return;
      for (const alt of alternatives(s)) {
        if (isObj(alt.properties)) {
          for (const [name, child] of Object.entries(alt.properties)) {
            if (!isObj(child)) continue;
            const p = pathOf(path, name);
            if (out.some((f) => f.path === p)) continue;
            // A failed system call's reason (ADR-0079) reads in plain words in both languages.
            const failed = name === "error" && child.description === ERROR.description;
            const title = failed ? (lang === "ar" ? "ما الذي حدث من خطأ" : "What went wrong") : typeof child.title === "string" ? child.title : humaniseName(name);
            const fieldLabel = [...label, title];
            out.push({ ...base, path: p, fieldLabel: fieldLabel.join(" › "), types: schemaTypes(child), description: describe(child), known: knownValues(child), depth, schema: child });
            walk(child, p, fieldLabel, depth + 1);
          }
        } else if (isObj(alt.items) && depth < maxDepth) {
          const p = pathOf(path, 0);
          walk(alt.items, p, [...label, lang === "ar" ? "كل عنصر" : "each item"], depth + 1);
        }
      }
    };
    walk(schema, key, [], 1);
  }
  return out;
}

/** The fields a step reads, as state paths (from its templates, variables and subject). */
export function referencedPaths(value: unknown): string[] {
  const out: string[] = [];
  const re = /\{\{\s*state\.([A-Za-z_][\w]*(?:(?:\.[A-Za-z_]\w*)|\[\d+\])*)\s*\}\}/g;
  const visit = (v: unknown) => {
    if (typeof v === "string") for (const m of v.matchAll(re)) out.push(m[1] as string);
    else if (Array.isArray(v)) v.forEach(visit);
    else if (isObj(v) && !("$literal" in v) && !("$file" in v)) Object.values(v).forEach(visit);
  };
  visit(value);
  return out;
}

/** Splits `search.clubs[0].name` into `["search", "clubs", 0, "name"]`. */
export function pathSegments(path: string): (string | number)[] {
  const out: (string | number)[] = [];
  for (const m of path.matchAll(/([A-Za-z_]\w*)|\[(\d+)\]/g)) out.push(m[1] !== undefined ? m[1] : Number(m[2]));
  return out;
}

/** The schema of a state path in the map (null when unknown). */
export function pathSchema(dm: DataMap, path: string): Schema | null {
  const [root, ...rest] = pathSegments(path);
  const vs = dm.values.filter((v) => v.key === root);
  if (!vs.length) return null;
  return schemaAt(union(vs.map((v) => v.schema)), rest);
}

/** Server issues for one step (or the agent when `node` is null). */
export function issuesFor(dm: DataMap | null | undefined, node: string | null): DataIssue[] {
  return (dm?.issues ?? []).filter((i) => i.node === node);
}
